/**
 * Talking to /api/proposal.
 *
 * The one place the browser knows the endpoint exists. Both sides import the
 * request and response types from src/shared/proposal.ts, so a change to the
 * contract breaks the build rather than a submission.
 *
 * Every failure path returns the same discriminated union the server returns,
 * so the form has exactly one shape to render and never has to tell a thrown
 * exception apart from a rejected request.
 */
import type { ProposalRequest, ProposalResponse } from '../../shared/proposal';

/**
 * How long to wait before giving up.
 *
 * The server sends two emails before it answers, so this is generous. Without
 * it a hung request leaves the button spinning forever, which reads as a broken
 * site rather than a slow one.
 */
const TIMEOUT_MS = 20_000;

export async function sendProposal(request: ProposalRequest): Promise<ProposalResponse> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const response = await fetch('/api/proposal', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
      signal: controller.signal,
    });

    /*
     * A rate limit or a validation failure is a normal answer with a JSON body,
     * so the status is not consulted here — `ok` in the body is what decides.
     * Only a response we cannot parse at all is treated as a server error.
     */
    const body: unknown = await response.json().catch(() => null);

    if (body && typeof body === 'object' && 'ok' in body) {
      return body as ProposalResponse;
    }

    return {
      ok: false,
      error: 'server_error',
      message: 'The server returned something we could not read.',
    };
  } catch {
    /*
     * A network failure, a DNS failure, or our own timeout. The visitor cannot
     * act on the difference, and the copy tells them to email instead — which
     * works in every one of those cases.
     */
    return {
      ok: false,
      error: 'send_failed',
      message: 'We could not reach the server.',
    };
  } finally {
    clearTimeout(timer);
  }
}
